/**
 * usePlacementSubmissions — TanStack Query hooks for the Ausschreibungs-Screen Tab 3b (Submission +
 * Versand + Erinnerung, Prozessschritte 6/7; P2 Placement Slice 8).
 *
 * Server state = TanStack Query ONLY. live mode hits apiClient.placements.*; mock mode resolves the
 * Ü3b fixture (AXA SUB-2026-000482-HAFT eingegangen · Zurich …483 eingegangen · Helvetia …484
 * versandt, Frist 16.10). The server owns every decision; the UI only triggers + renders. The
 * mutations invalidate the submissions + ranking + auswahl + detail keys (a send transitions the
 * placement and locks the upstream tiles).
 *
 * [Source: apps/web/src/features/placements/usePlacementVersicherer.ts] (live/mock + qk + invalidate idiom)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import {
  chipsMitSubmission,
  submissionZaehler,
  SUBMISSION_STATUS_LABELS,
  VERSICHERER_AUSWAHL_STATUS,
} from '@brokly/shared';
import type {
  PlacementCarrierChip,
  SubmissionEintrag,
  SubmissionErinnernBody,
  SubmissionInaktivBody,
  SubmissionListResponse,
  SubmissionNichtOfferiertBody,
  SubmissionStatus,
  SubmissionVersendenBody,
} from '@brokly/shared';

// ── Ü3b mock fixture (mock mode only) ─────────────────────────────────────────────

function mockEintrag(
  uid: string,
  name: string,
  number: string,
  status: SubmissionStatus,
  over: Partial<SubmissionEintrag> = {},
): SubmissionEintrag {
  return {
    id: `sub-${uid}`,
    placement_id: 'mock',
    carrier_selection_id: `sel-${uid}`,
    carrier_uid: uid,
    name,
    logo_url: null,
    submission_number: number,
    status,
    status_label: SUBMISSION_STATUS_LABELS[status],
    status_changed_at: '2026-10-02T08:00:00.000Z',
    carrier_email_to: `offerten@${uid.toLowerCase()}.ch`,
    email_aktuell: `offerten@${uid.toLowerCase()}.ch`,
    submission_faehig: true,
    frist_date: over.frist_date ?? '2026-10-16',
    frist_abgelaufen: false,
    frist_eskaliert_at: null,
    frist_task_id: null,
    sent_at: '2026-10-02T08:00:00.000Z',
    sent_by: null,
    sent_by_name: 'Max Muster',
    erinnert_count: 0,
    letzte_erinnerung_at: null,
    received_at: null,
    nicht_offeriert_at: null,
    nicht_offeriert_grund: null,
    inaktiv_at: null,
    inaktiv_grund: null,
    versand_laeuft: false,
    versand_unterbrochen: false,
    versand_gesperrt: false,
    nachtragen_moeglich: false,
    letzter_versandfehler: null,
    letzter_versandfehler_at: null,
    erlaubte_transitionen: [],
    emails: [],
    attachments: [],
    created_at: '2026-10-02T08:00:00.000Z',
    updated_at: '2026-10-02T08:00:00.000Z',
    ...over,
  };
}

function mockSubmissions(id: string): SubmissionListResponse {
  const items: SubmissionEintrag[] = [
    mockEintrag('CHE-AXA', 'AXA', 'SUB-2026-000482-HAFT', 'eingegangen', {
      received_at: '2026-10-11T10:00:00.000Z',
      erinnert_count: 1,
      letzte_erinnerung_at: '2026-10-09T09:14:00.000Z',
      emails: [
        { id: 'em-axa-1', art: 'versand', communication_id: 'comm-axa-1', message_id: '<axa-1@broker.ch>', empfaenger: 'offerten@che-axa.ch', betreff: 'Offertanfrage SUB-2026-000482-HAFT', attachment_count: 4, sent_at: '2026-10-02T08:00:00.000Z', sent_by: null, sent_by_name: 'Max Muster', bounce_at: null, bounce_grund: null },
        { id: 'em-axa-2', art: 'erinnerung', communication_id: 'comm-axa-2', message_id: '<axa-2@broker.ch>', empfaenger: 'offerten@che-axa.ch', betreff: 'Erinnerung: Offertanfrage SUB-2026-000482-HAFT', attachment_count: 0, sent_at: '2026-10-09T09:14:00.000Z', sent_by: null, sent_by_name: 'Max Muster', bounce_at: null, bounce_grund: null },
      ],
    }),
    mockEintrag('CHE-ZUR', 'Zurich', 'SUB-2026-000483-HAFT', 'eingegangen', {
      received_at: '2026-10-12T10:00:00.000Z',
    }),
    mockEintrag('CHE-HEL', 'Helvetia', 'SUB-2026-000484-HAFT', 'versandt'),
  ];
  const baseChips: PlacementCarrierChip[] = items.map((it) => ({
    carrier_uid: it.carrier_uid,
    name: it.name,
    logo_url: it.logo_url,
    status: VERSICHERER_AUSWAHL_STATUS.AUSGEWAEHLT,
    status_label: 'ausgewählt',
  }));
  return {
    placement_id: id,
    placement_number: 'PL-2026-000001',
    status: 'ausgeschrieben',
    branchencode: '21000',
    kuerzel: 'HAFT',
    aktion_erlaubt: true,
    sperrgrund: null,
    erstellbar: 0,
    ...submissionZaehler(items),
    mailbox_verbunden: true,
    frist_default_date: '2026-10-16',
    items,
    chips: chipsMitSubmission(baseChips, items),
  };
}

// ── Query ──────────────────────────────────────────────────────────────────────

export function usePlacementSubmissions(id: string) {
  const live = isLiveData();
  return useQuery<SubmissionListResponse>({
    queryKey: qk.placementSubmissions(id, live ? 'live' : 'mock'),
    queryFn: () => (live ? apiClient.placements.getSubmissions(id) : Promise.resolve(mockSubmissions(id))),
    ...(live ? {} : { initialData: () => mockSubmissions(id), staleTime: Infinity }),
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────

function useSubmissionInvalidate(id: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['placements', 'submissions', id] });
    void qc.invalidateQueries({ queryKey: ['placements', 'versicherer-ranking', id] });
    void qc.invalidateQueries({ queryKey: ['placements', 'versicherer-auswahl', id] });
    void qc.invalidateQueries({ queryKey: ['placements', 'detail', id] });
  };
}

export function useErstellenSubmissions(id: string) {
  const live = isLiveData();
  const invalidate = useSubmissionInvalidate(id);
  return useMutation({
    mutationFn: () =>
      live ? apiClient.placements.erstellenSubmissions(id) : Promise.resolve({ ...mockSubmissions(id), erstellt: [], uebersprungen: [] }),
    onSuccess: invalidate,
  });
}

export function useVersendenSubmissions(id: string) {
  const live = isLiveData();
  const invalidate = useSubmissionInvalidate(id);
  return useMutation({
    mutationFn: (body: SubmissionVersendenBody) =>
      live
        ? apiClient.placements.versendenSubmissions(id, body)
        : Promise.resolve({ ...mockSubmissions(id), versendet: [], fehlgeschlagen: [], uebersprungen: [], abgebrochen: false, placement_transitioned: false, hinweise: [] }),
    onSuccess: invalidate,
  });
}

export function useErinnernSubmission(id: string) {
  const live = isLiveData();
  const invalidate = useSubmissionInvalidate(id);
  return useMutation({
    mutationFn: ({ sid, body }: { sid: string; body: SubmissionErinnernBody }) =>
      live
        ? apiClient.placements.erinnernSubmission(id, sid, body)
        : Promise.resolve({ ...mockSubmissions(id), versendet: [], fehlgeschlagen: [], uebersprungen: [], abgebrochen: false, placement_transitioned: false, hinweise: [] }),
    onSuccess: invalidate,
  });
}

export function useNichtOfferiertSubmission(id: string) {
  const live = isLiveData();
  const invalidate = useSubmissionInvalidate(id);
  return useMutation({
    mutationFn: ({ sid, body }: { sid: string; body: SubmissionNichtOfferiertBody }) =>
      live ? apiClient.placements.nichtOfferiertSubmission(id, sid, body) : Promise.resolve(mockSubmissions(id)),
    onSuccess: invalidate,
  });
}

export function useVersandAufloesenSubmission(id: string) {
  const live = isLiveData();
  const invalidate = useSubmissionInvalidate(id);
  return useMutation({
    mutationFn: ({ sid, aktion }: { sid: string; aktion: 'nachtragen' | 'freigeben' }) =>
      live ? apiClient.placements.versandAufloesenSubmission(id, sid, aktion) : Promise.resolve(mockSubmissions(id)),
    onSuccess: invalidate,
  });
}

export function useInaktivSubmission(id: string) {
  const live = isLiveData();
  const invalidate = useSubmissionInvalidate(id);
  return useMutation({
    mutationFn: ({ sid, body }: { sid: string; body: SubmissionInaktivBody }) =>
      live ? apiClient.placements.inaktivSubmission(id, sid, body) : Promise.resolve(mockSubmissions(id)),
    onSuccess: invalidate,
  });
}

export function usePatchSubmissionFrist(id: string) {
  const live = isLiveData();
  const invalidate = useSubmissionInvalidate(id);
  return useMutation({
    mutationFn: ({ sid, frist_date }: { sid: string; frist_date: string }) =>
      live ? apiClient.placements.patchSubmissionFrist(id, sid, { frist_date }) : Promise.resolve(mockSubmissions(id)),
    onSuccess: invalidate,
  });
}
